import type { Control, FieldPath } from "react-hook-form";
import { z } from "zod";
import { Input } from "@/app/global-components/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/global-components/form";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { createResetPasswordSchema } from "@/schema";
import useForgotPassword from "@/hooks/use-forgot-password";

const ResetPasswordSchema = createResetPasswordSchema((key: string) => key);

interface ResetPasswordFormFieldViewProps {
  name: FieldPath<z.infer<typeof ResetPasswordSchema>>;
  label: string;
  email: string;
  placeholder: string;
  description?: string;
  inputType?: string;
  formControl: Control<z.infer<typeof ResetPasswordSchema>>;
}

const ResetPasswordFormFieldView: React.FC<ResetPasswordFormFieldViewProps> = ({
  name,
  email,
  label,
  placeholder,
  inputType,
  formControl,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const { sendForgotPasswordEmail, isLoading } = useForgotPassword();

  const handleResendCode = async () => {
    try {
      await sendForgotPasswordEmail({ email });
    } catch (err) {}
  };

  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field }) => (
        <FormItem>
          {name === "code" ? (
            <div className="flex justify-between items-center mb-2">
              <FormLabel className="float-right">{label}</FormLabel>
              <p className="text-sm text-textcolor-mediumGray">
                <span
                  className="underline text-textcolor-black cursor-pointer"
                  onClick={handleResendCode}
                  style={{ pointerEvents: isLoading ? "none" : "auto" }}
                >
                  {isLoading ? t("status.sending") : t("actions.resendCode")}
                </span>
              </p>
            </div>
          ) : (
            <FormLabel className="float-right mb-2">{label}</FormLabel>
          )}
          <FormControl>
            <Input
              className="focus-visible:ring-secondary-brightYellow"
              placeholder={placeholder}
              type={inputType || "text"}
              {...field}
            />
          </FormControl>
          <FormMessage className="text-red-500" />
        </FormItem>
      )}
    />
  );
};

export default ResetPasswordFormFieldView;
